import { FC, ReactElement } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Typography,
    Avatar,
    Box,
    Stack,
    CircularProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Remove as RemoveIcon,
    ContentCopy as CopyIcon,
    EventNote as TemplateSetIcon,
    DeleteSweep as ClearIcon,
} from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import { useGetScheduleAuditQuery } from '@/store/apiSlice';
import { getApiErrorMessage } from '@/utils/apiError';
import type { ScheduleAuditEntry } from '@/types';

interface ScheduleAuditDialogProps {
    open: boolean;
    onClose: () => void;
    date: string;
}

const ACTION_STYLE: Record<ScheduleAuditEntry['action'], [string, ReactElement]> = {
    create: ['success.main', <AddIcon fontSize="small" />],
    update: ['info.main', <EditIcon fontSize="small" />],
    delete: ['error.main', <RemoveIcon fontSize="small" />],
    copy: ['secondary.main', <CopyIcon fontSize="small" />],
    'apply-template-set': ['secondary.main', <TemplateSetIcon fontSize="small" />],
    clear: ['error.main', <ClearIcon fontSize="small" />],
};

const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });

const show = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

const ScheduleAuditDialog: FC<ScheduleAuditDialogProps> = ({ open, onClose, date }) => {
    const { LL } = useI18nContext();
    const { data = [], isLoading, error } = useGetScheduleAuditQuery({ date }, { skip: !open });

    const fieldLabel: Record<string, string> = {
        title: LL.scheduleModal.pdfTopic(),
        duration: LL.scheduleModal.pdfDuration(),
        responsible: LL.scheduleModal.pdfResponsible(),
        note: LL.scheduleModal.pdfNote(),
        tags: LL.scheduleModal.tagsLabel(),
    };

    const describe = (row: ScheduleAuditEntry) => {
        const title = row.title ?? '';
        const count = row.meta?.count ?? 0;
        switch (row.action) {
            case 'create':
                return LL.scheduleModal.auditCreate({ title });
            case 'update':
                return LL.scheduleModal.auditUpdate({ title });
            case 'delete':
                return LL.scheduleModal.auditDelete({ title });
            case 'copy':
                return LL.scheduleModal.auditCopy({
                    count,
                    fromDate: new Date(row.meta?.fromDate ?? '').toLocaleDateString(),
                });
            case 'apply-template-set':
                return LL.scheduleModal.auditApplyTemplateSet({
                    setName: row.meta?.setName ?? '',
                    count,
                });
            case 'clear':
                return LL.scheduleModal.auditClear({ count });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{LL.scheduleModal.changeHistory()}</DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <Stack sx={{ py: 4, alignItems: 'center' }}>
                        <CircularProgress />
                    </Stack>
                ) : error ? (
                    <Typography color="error" variant="body2">
                        {getApiErrorMessage(error)}
                    </Typography>
                ) : data.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {LL.audit.empty()}
                    </Typography>
                ) : (
                    <List dense disablePadding>
                        {data.map((row) => {
                            const [color, icon] = ACTION_STYLE[row.action] ?? ACTION_STYLE.update;
                            return (
                                <ListItem
                                    key={row.id}
                                    disableGutters
                                    sx={{ alignItems: 'flex-start' }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            mr: 1,
                                            mt: 0.5,
                                            bgcolor: color,
                                            color: 'common.white',
                                        }}
                                    >
                                        {icon}
                                    </Avatar>
                                    <ListItemText
                                        primary={describe(row)}
                                        secondary={
                                            <>
                                                {Object.entries(row.meta?.changes ?? {}).map(
                                                    ([field, [from, to]]) => (
                                                        <Box
                                                            component="span"
                                                            key={field}
                                                            sx={{ display: 'block' }}
                                                        >
                                                            {fieldLabel[field] ?? field}:{' '}
                                                            {show(from)} → {show(to)}
                                                        </Box>
                                                    )
                                                )}
                                                {formatDateTime(row.createdAt)} · {LL.audit.by()}:{' '}
                                                {row.actorResourceName ?? '—'}
                                            </>
                                        }
                                        slotProps={{ primary: { sx: { fontWeight: 600 } } }}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{LL.common.close()}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleAuditDialog;
